/**
 * DriverDiagnoseButton — stethoscope icon → modal explaining exactly why
 * the dispatch engine considers a driver (in)eligible right now, plus
 * a one-click "Force-release" for stale ride references.
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Behaviour is BYTE-IDENTICAL: same testids, same fetch path
 * (/admin/driver-eligibility/{id} + POST /admin/force-release/{id}),
 * same verdict palette (#10B981 / #EF4444), same window.confirm() gate
 * on the force-release.
 */
import React, { useState } from "react";
import { Stethoscope, X, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";

export const DriverDiagnoseButton = ({ driverId, driverName }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const fetchDiag = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/driver-eligibility/${driverId}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchDiag();
  };

  const handleForceRelease = async () => {
    if (!window.confirm(`Force-release ${driverName}? This will cancel any non-final rides attached to them.`)) return;
    setReleasing(true);
    try {
      const { data } = await api.post(`/admin/force-release/${driverId}`);
      toast.success(`Released ${data.released_rides?.length || 0} ride(s) from ${driverName}`);
      fetchDiag();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReleasing(false); }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="text-muted-foreground hover:text-[#002FA7] p-2"
        data-testid={`diagnose-driver-${driverId}`}
        title="Diagnose dispatch eligibility"
      >
        <Stethoscope className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border-2 border-border p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="diagnose-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mini text-muted-foreground">DISPATCH ELIGIBILITY</div>
                <h3 className="font-heading text-lg font-black">{driverName}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="diagnose-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

            {data && (
              <>
                <div
                  className="border-2 p-3"
                  style={{ borderColor: data.eligible ? "#10B981" : "#EF4444", backgroundColor: data.eligible ? "#ECFDF5" : "#FEF2F2" }}
                  data-testid="diagnose-verdict"
                  data-eligible={String(data.eligible)}
                  data-reason={data.reason}
                >
                  <div className="font-heading text-xl font-black">
                    {data.eligible ? "✓ ELIGIBLE" : "✗ NOT ELIGIBLE"}
                  </div>
                  <div className="text-sm font-semibold mt-1" style={{ color: data.eligible ? "#065F46" : "#991B1B" }}>
                    {data.reason.replace(/_/g, " ").toUpperCase()}
                  </div>
                  {data.evidence && Object.keys(data.evidence).length > 0 && (
                    <pre className="text-[10px] text-muted-foreground mt-2 font-mono-tab whitespace-pre-wrap break-all">
                      {JSON.stringify(data.evidence, null, 2)}
                    </pre>
                  )}
                </div>

                <div className="space-y-1 text-xs">
                  <div className="label-mini text-muted-foreground">DRIVER STATE</div>
                  <div className="font-mono-tab">
                    online: <span className="font-bold">{String(data.driver.is_online)}</span> ·
                    location: {data.driver.current_lat ? `${data.driver.current_lat.toFixed(3)}, ${data.driver.current_lng.toFixed(3)}` : "none"}
                  </div>
                  {data.driver.last_seen && (
                    <div className="font-mono-tab text-muted-foreground">last_seen: {new Date(data.driver.last_seen).toLocaleString()}</div>
                  )}
                </div>

                <div className="space-y-1 text-xs">
                  <div className="label-mini text-muted-foreground">NON-FINAL RIDES LINKED ({data.attached_non_final_rides.length})</div>
                  {data.attached_non_final_rides.length === 0 ? (
                    <div className="text-muted-foreground italic">None — driver has a clean slate.</div>
                  ) : (
                    <div className="space-y-1">
                      {data.attached_non_final_rides.map((r) => (
                        <div key={r.id} className="border border-border px-2 py-1 font-mono-tab" data-testid={`attached-ride-${r.id}`}>
                          <span className="font-bold">{r.id.substring(0, 8)}</span> ·{" "}
                          <span style={{ color: "#F59E0B" }}>{r.status}</span>{" "}
                          {r.driver_id === driverId ? "(driver_id)" : "(offered_to)"}
                          {r.passenger_name && <span className="text-muted-foreground"> · {r.passenger_name}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2 border-t border-border">
                  <button
                    onClick={fetchDiag}
                    className="flex-1 text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors flex items-center justify-center gap-1"
                    data-testid="diagnose-refresh"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                  {data.attached_non_final_rides.length > 0 && (
                    <button
                      onClick={handleForceRelease}
                      disabled={releasing}
                      className="flex-1 text-xs px-3 py-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold transition-colors disabled:opacity-60"
                      data-testid="force-release-btn"
                    >
                      Force-release
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DriverDiagnoseButton;
